import { TableColumn } from './dynamic-table/dynamic-table.component';

/**
 * Filters the column definition list and cleans the data rows.
 *
 *  • Required columns are always kept.
 *  • Optional columns are kept only when at least one row has a
 *    non‑empty value (null/undefined/empty‑string are treated as empty).
 *  • After the decision is made, every row gets all kept column ids.
 *    Missing keys are added with "null".
 *  • All keys that are *not* part of the kept column set are deleted.
 *
 * @param columnDef   Original column definition array – will be mutated
 * @param data        Table rows – will be mutated (deep‑clone if you need immutability)
 * @returns an object with the modified
 */
export function filterOptionalData(columnDef: TableColumn[], data: Record<string, unknown>[]) {
  const keptColumns = columnDef.filter(col => {
    // required → always keep
    if (!col.isOptional) return true;

    // optional → keep if any row contains a non‑empty value
    return data.some(row => {
      const v = row[col.id];
      // “empty” = undefined | null | '' (or only whitespace)
      return v !== undefined && v !== null && !(typeof v === 'string' && v.trim() === '');
    });
  });

  // update the original columnDef reference – the caller expects it to be changed
  columnDef.splice(0, columnDef.length, ...keptColumns);

  const keptIds = new Set(keptColumns.map(col => col.id));
  data.forEach(row => {
    // add the missing keys
    keptIds.forEach(id => {
      if (!(id in row)) {
        row[id] = null;
      }
    });

    // Delete every property whose key is not in the kept set
    Object.keys(row).forEach(key => {
      if (!keptIds.has(key)) {
        delete row[key];
      }
    });
  });
  return { columnDef: columnDef, data: data };
}
